import { useEffect, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  AlertCircle,
  Globe,
  Layers,
  Loader2,
  Package,
  Percent,
  Plus,
  Search,
  Trash2,
  Wrench,
  X,
} from 'lucide-react';
import { commissionApi, posApi } from '@/services/api';
import type { CommissionRule } from '@/types';

type CommissionTier = NonNullable<CommissionRule['tiers']>[number];
type ApplyScope = NonNullable<CommissionRule['apply_scope']>;

interface CommissionRuleFormModalProps {
  isOpen: boolean;
  onClose: () => void;
  editing: CommissionRule | null;
}

interface PosItemOption {
  name: string;
  item_name: string;
  item_type?: string;
}

const emptyRule = (): CommissionRule => ({
  rule_name: '',
  is_active: true,
  rule_type: 'Flat Percent',
  basis: 'Net Sales',
  rate: 2,
  priority: 100,
  apply_scope: 'All Sales',
  rule_items: [],
  rule_categories: [],
  tiers: [],
});

const emptyTier = (fromAmount = 0, rate = 2): CommissionTier => ({
  from_amount: fromAmount,
  to_amount: undefined,
  rate,
});

const SCOPE_OPTIONS: { id: ApplyScope; icon: typeof Globe }[] = [
  { id: 'All Sales', icon: Globe },
  { id: 'Category', icon: Layers },
  { id: 'Products', icon: Package },
  { id: 'Services', icon: Wrench },
  { id: 'Specific Items', icon: Search },
];

const SCOPE_I18N: Record<ApplyScope, { label: string; desc: string }> = {
  'All Sales': { label: 'allSales', desc: 'allSalesDesc' },
  Category: { label: 'category', desc: 'categoryDesc' },
  Products: { label: 'products', desc: 'productsDesc' },
  Services: { label: 'services', desc: 'servicesDesc' },
  'Specific Items': { label: 'specificItems', desc: 'specificItemsDesc' },
};

function nextTierFromAmount(tiers: CommissionTier[]): number {
  if (!tiers.length) return 0;
  const last = tiers[tiers.length - 1];
  if (last.to_amount != null && last.to_amount > 0) {
    return last.to_amount + 1;
  }
  return last.from_amount;
}

const RULE_TYPES: CommissionRule['rule_type'][] = ['Flat Percent', 'Margin Percent', 'Fixed Per Unit', 'Tiered Monthly'];
const BASIS_TYPES: CommissionRule['basis'][] = ['Net Sales', 'Gross Margin', 'Quantity'];

export default function CommissionRuleFormModal({ isOpen, onClose, editing }: CommissionRuleFormModalProps) {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const [form, setForm] = useState<CommissionRule>(emptyRule());
  const [error, setError] = useState('');
  const [itemSearch, setItemSearch] = useState('');
  const [categorySearch, setCategorySearch] = useState('');

  useEffect(() => {
    if (!isOpen) return;
    if (editing) {
      setForm({
        ...emptyRule(),
        ...editing,
        apply_scope: editing.apply_scope || (editing.item ? 'Specific Items' : editing.item_category || editing.rule_categories?.length ? 'Category' : 'All Sales'),
        tiers: editing.tiers?.length ? [...editing.tiers] : [],
        rule_items: editing.rule_items?.length ? [...editing.rule_items] : [],
        rule_categories: editing.rule_categories?.length
          ? [...editing.rule_categories]
          : editing.item_category
            ? [{ item_category: editing.item_category, category_name: editing.item_category_name }]
            : [],
      });
    } else {
      setForm(emptyRule());
    }
    setItemSearch('');
    setCategorySearch('');
    setError('');
  }, [isOpen, editing]);

  const { data: categories = [] } = useQuery({
    queryKey: ['commissionRuleCategories'],
    queryFn: async () => {
      const res = await posApi.getCategories();
      return res.data.message as { name: string; category_name: string; category_type?: string }[];
    },
    enabled: isOpen,
  });

  const { data: itemResults = [], isFetching: itemsLoading } = useQuery({
    queryKey: ['commissionRuleItemSearch', itemSearch],
    queryFn: async () => {
      const res = await posApi.getItems({ search: itemSearch.trim(), limit: 30 });
      const payload = res.data.message;
      const rows = (payload?.items ?? payload ?? []) as PosItemOption[];
      return rows;
    },
    enabled: isOpen && form.apply_scope === 'Specific Items' && itemSearch.trim().length >= 2,
  });

  const selectedItemIds = useMemo(
    () => new Set((form.rule_items ?? []).map((row) => row.item)),
    [form.rule_items]
  );

  const selectedCategoryIds = useMemo(
    () => new Set((form.rule_categories ?? []).map((row) => row.item_category)),
    [form.rule_categories]
  );

  const filteredCategories = useMemo(() => {
    const q = categorySearch.trim().toLowerCase();
    return categories.filter((cat) => {
      if (selectedCategoryIds.has(cat.name)) return false;
      if (!q) return true;
      return (
        cat.category_name.toLowerCase().includes(q) ||
        cat.name.toLowerCase().includes(q) ||
        (cat.category_type ?? '').toLowerCase().includes(q)
      );
    });
  }, [categories, categorySearch, selectedCategoryIds]);

  const saveMutation = useMutation({
    mutationFn: () => {
      const payload: CommissionRule = { ...form, name: editing?.name };
      if (applyScope === 'Category' && payload.rule_categories?.length) {
        payload.item_category = payload.rule_categories[0].item_category;
      }
      return commissionApi.saveRule(payload as unknown as Record<string, unknown>);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['commissionRules'] });
      onClose();
    },
    onError: (err: unknown) => {
      const msg = (err as { response?: { data?: { message?: string } } })?.response?.data?.message;
      setError(msg || t('commission.form.saveFailed'));
    },
  });

  const tiers = form.tiers ?? [];
  const applyScope = form.apply_scope || 'All Sales';

  const handleScopeChange = (scope: ApplyScope) => {
    setForm((prev) => ({
      ...prev,
      apply_scope: scope,
      item_category: undefined,
      item: undefined,
      rule_categories: scope === 'Category' ? prev.rule_categories ?? [] : [],
      rule_items: scope === 'Specific Items' ? prev.rule_items ?? [] : [],
    }));
    setItemSearch('');
    setCategorySearch('');
    setError('');
  };

  const addRuleCategory = (cat: { name: string; category_name: string; category_type?: string }) => {
    if (selectedCategoryIds.has(cat.name)) return;
    setForm((prev) => ({
      ...prev,
      rule_categories: [
        ...(prev.rule_categories ?? []),
        { item_category: cat.name, category_name: cat.category_name },
      ],
    }));
  };

  const removeRuleCategory = (categoryId: string) => {
    setForm((prev) => ({
      ...prev,
      rule_categories: (prev.rule_categories ?? []).filter((row) => row.item_category !== categoryId),
    }));
  };

  const addRuleItem = (item: PosItemOption) => {
    if (selectedItemIds.has(item.name)) return;
    setForm((prev) => ({
      ...prev,
      rule_items: [...(prev.rule_items ?? []), { item: item.name, item_name: item.item_name }],
    }));
    setItemSearch('');
  };

  const removeRuleItem = (itemId: string) => {
    setForm((prev) => ({
      ...prev,
      rule_items: (prev.rule_items ?? []).filter((row) => row.item !== itemId),
    }));
  };

  const handleRuleTypeChange = (ruleType: CommissionRule['rule_type']) => {
    setForm((prev) => {
      const next: CommissionRule = { ...prev, rule_type: ruleType };
      if (ruleType === 'Tiered Monthly' && !(prev.tiers?.length)) {
        next.tiers = [emptyTier()];
      }
      if (ruleType !== 'Tiered Monthly') {
        next.tiers = [];
      }
      return next;
    });
  };

  const updateTier = (index: number, patch: Partial<CommissionTier>) => {
    setForm((prev) => {
      const nextTiers = [...(prev.tiers ?? [])];
      nextTiers[index] = { ...nextTiers[index], ...patch };
      return { ...prev, tiers: nextTiers };
    });
  };

  const removeTier = (index: number) => {
    setForm((prev) => ({
      ...prev,
      tiers: (prev.tiers ?? []).filter((_, i) => i !== index),
    }));
  };

  const addTier = () => {
    const last = tiers[tiers.length - 1];
    if (last && (last.to_amount == null || last.to_amount <= 0)) {
      setError(t('commission.form.tierToRequired'));
      return;
    }
    setError('');
    setForm((prev) => ({
      ...prev,
      tiers: [...(prev.tiers ?? []), emptyTier(nextTierFromAmount(prev.tiers ?? []), last?.rate ?? 2)],
    }));
  };

  const validate = (): string | null => {
    if (!form.rule_name.trim()) return t('commission.form.ruleNameRequired');
    if (applyScope === 'Category' && !(form.rule_categories?.length)) {
      return t('commission.applyScope.categoryRequired');
    }
    if (applyScope === 'Specific Items' && !(form.rule_items?.length)) {
      return t('commission.applyScope.itemsRequired');
    }
    if (form.rule_type === 'Tiered Monthly') {
      if (!tiers.length) return t('commission.form.tiersRequired');
      for (let i = 0; i < tiers.length; i++) {
        const tier = tiers[i];
        const n = i + 1;
        if (tier.from_amount < 0) return t('commission.form.tierFromMin', { n });
        if (tier.rate <= 0) return t('commission.form.tierRateMin', { n });
        if (tier.to_amount != null && tier.to_amount > 0 && tier.to_amount <= tier.from_amount) {
          return t('commission.form.tierToGreater', { n });
        }
        if (i > 0 && tier.from_amount <= (tiers[i - 1].from_amount ?? 0)) {
          return t('commission.form.tierFromGreater', { n });
        }
      }
    } else if (form.rule_type === 'Fixed Per Unit') {
      if (!form.fixed_amount || form.fixed_amount <= 0) return t('commission.form.fixedRequired');
    } else if (form.rate <= 0) {
      return t('commission.form.rateRequired');
    }
    return null;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const validationError = validate();
    if (validationError) {
      setError(validationError);
      return;
    }
    setError('');
    saveMutation.mutate();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
          <div className="flex items-center gap-2">
            <Percent className="w-5 h-5 text-primary-600" />
            <h2 className="text-lg font-semibold text-gray-900">
              {editing ? t('commission.form.editTitle') : t('commission.form.addTitle')}
            </h2>
          </div>
          <button type="button" onClick={onClose} className="p-1.5 rounded-lg hover:bg-gray-100 text-gray-500">
            <X className="w-5 h-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="overflow-y-auto flex-1 p-5 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-2">
              <label className="text-sm font-medium text-gray-700">{t('commission.form.ruleName')}</label>
              <input
                className="input-field mt-1"
                placeholder={t('commission.form.ruleNamePlaceholder')}
                value={form.rule_name}
                onChange={(e) => setForm({ ...form, rule_name: e.target.value })}
              />
            </div>
          </div>

          <div className="space-y-3">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">{t('commission.applyScope.title')}</h3>
              <p className="text-xs text-gray-500 mt-0.5">{t('commission.applyScope.subtitle')}</p>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
              {SCOPE_OPTIONS.map(({ id, icon: Icon }) => (
                <button
                  key={id}
                  type="button"
                  onClick={() => handleScopeChange(id)}
                  className={`text-left rounded-xl border p-3 transition-colors ${
                    applyScope === id
                      ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500'
                      : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-start gap-2">
                    <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${applyScope === id ? 'text-primary-600' : 'text-gray-500'}`} />
                    <div>
                      <p className={`text-sm font-medium ${applyScope === id ? 'text-primary-900' : 'text-gray-900'}`}>
                        {t(`commission.applyScope.${SCOPE_I18N[id].label}`)}
                      </p>
                      <p className="text-xs text-gray-500 mt-0.5 leading-snug">
                        {t(`commission.applyScope.${SCOPE_I18N[id].desc}`)}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>

            {applyScope === 'Category' && (
              <div className="space-y-3 rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                <label className="text-sm font-medium text-gray-700">{t('commission.applyScope.selectCategories')}</label>
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input-field pl-9"
                    value={categorySearch}
                    onChange={(e) => setCategorySearch(e.target.value)}
                    placeholder={t('commission.applyScope.searchCategories')}
                  />
                </div>
                {filteredCategories.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                    {filteredCategories.map((cat) => (
                      <button
                        key={cat.name}
                        type="button"
                        className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                        onClick={() => addRuleCategory(cat)}
                      >
                        <span className="text-sm text-gray-900">{cat.category_name}</span>
                        <span className="text-xs text-gray-500 shrink-0">{cat.category_type}</span>
                      </button>
                    ))}
                  </div>
                )}
                {categorySearch.trim() && filteredCategories.length === 0 && (
                  <p className="text-xs text-gray-500">{t('commission.applyScope.noCategoriesFound')}</p>
                )}
                {(form.rule_categories?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">{t('commission.applyScope.selectedCategories')}</p>
                    <div className="flex flex-wrap gap-2">
                      {form.rule_categories!.map((row) => (
                        <span
                          key={row.item_category}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 text-sm text-gray-800"
                        >
                          {row.category_name || row.item_category}
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => removeRuleCategory(row.item_category)}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {applyScope === 'Specific Items' && (
              <div className="space-y-3 rounded-xl border border-gray-200 p-4 bg-gray-50/50">
                <label className="text-sm font-medium text-gray-700">{t('commission.applyScope.searchItems')}</label>
                <div className="relative">
                  <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 -translate-y-1/2" />
                  <input
                    className="input-field pl-9"
                    value={itemSearch}
                    onChange={(e) => setItemSearch(e.target.value)}
                    placeholder={t('commission.applyScope.searchItems')}
                  />
                </div>
                {itemsLoading && (
                  <div className="flex items-center gap-2 text-sm text-gray-500">
                    <Loader2 className="w-4 h-4 animate-spin" />
                    …
                  </div>
                )}
                {itemSearch.trim().length >= 2 && itemResults.length > 0 && (
                  <div className="max-h-40 overflow-y-auto rounded-lg border border-gray-200 bg-white divide-y divide-gray-100">
                    {itemResults
                      .filter((item) => !selectedItemIds.has(item.name))
                      .map((item) => (
                        <button
                          key={item.name}
                          type="button"
                          className="w-full text-left px-3 py-2 hover:bg-gray-50 flex items-center justify-between gap-2"
                          onClick={() => addRuleItem(item)}
                        >
                          <span className="text-sm text-gray-900">{item.item_name}</span>
                          <span className="text-xs text-gray-500 shrink-0">{item.item_type}</span>
                        </button>
                      ))}
                  </div>
                )}
                {(form.rule_items?.length ?? 0) > 0 && (
                  <div>
                    <p className="text-xs font-medium text-gray-600 mb-2">{t('commission.applyScope.selectedItems')}</p>
                    <div className="flex flex-wrap gap-2">
                      {form.rule_items!.map((row) => (
                        <span
                          key={row.item}
                          className="inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-white border border-gray-200 text-sm text-gray-800"
                        >
                          {row.item_name || row.item}
                          <button
                            type="button"
                            className="text-gray-400 hover:text-red-500"
                            onClick={() => removeRuleItem(row.item)}
                          >
                            <X className="w-3.5 h-3.5" />
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="space-y-3 border-t border-gray-100 pt-4">
            <h3 className="text-sm font-semibold text-gray-900">{t('commission.form.ruleType')}</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-gray-700">{t('commission.form.ruleType')}</label>
                <select
                  className="input-field mt-1"
                  value={form.rule_type}
                  onChange={(e) => handleRuleTypeChange(e.target.value as CommissionRule['rule_type'])}
                >
                  {RULE_TYPES.map((type) => (
                    <option key={type} value={type}>
                      {t(`commission.ruleTypes.${type}`, { defaultValue: type })}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">{t('commission.form.basis')}</label>
                <select
                  className="input-field mt-1"
                  value={form.basis}
                  onChange={(e) => setForm({ ...form, basis: e.target.value as CommissionRule['basis'] })}
                >
                  {BASIS_TYPES.map((basis) => (
                    <option key={basis} value={basis}>
                      {t(`commission.basisTypes.${basis}`, { defaultValue: basis })}
                    </option>
                  ))}
                </select>
              </div>

              {form.rule_type !== 'Tiered Monthly' && form.rule_type !== 'Fixed Per Unit' && (
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('commission.form.rate')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="input-field mt-1"
                    value={form.rate}
                    onChange={(e) => setForm({ ...form, rate: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}

              {form.rule_type === 'Fixed Per Unit' && (
                <div>
                  <label className="text-sm font-medium text-gray-700">{t('commission.form.fixedAmount')}</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    className="input-field mt-1"
                    value={form.fixed_amount ?? ''}
                    onChange={(e) => setForm({ ...form, fixed_amount: parseFloat(e.target.value) || 0 })}
                  />
                </div>
              )}

              <div>
                <label className="text-sm font-medium text-gray-700">{t('commission.form.priority')}</label>
                <input
                  type="number"
                  className="input-field mt-1"
                  value={form.priority ?? 100}
                  onChange={(e) => setForm({ ...form, priority: parseInt(e.target.value, 10) || 100 })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">{t('commission.form.validFrom')}</label>
                <input
                  type="date"
                  className="input-field mt-1"
                  value={form.valid_from ?? ''}
                  onChange={(e) => setForm({ ...form, valid_from: e.target.value || undefined })}
                />
              </div>

              <div>
                <label className="text-sm font-medium text-gray-700">{t('commission.form.validTo')}</label>
                <input
                  type="date"
                  className="input-field mt-1"
                  value={form.valid_to ?? ''}
                  onChange={(e) => setForm({ ...form, valid_to: e.target.value || undefined })}
                />
              </div>

              <div className="md:col-span-2">
                <label className="text-sm font-medium text-gray-700">{t('commission.form.description')}</label>
                <textarea
                  className="input-field mt-1 min-h-[72px]"
                  value={form.description ?? ''}
                  onChange={(e) => setForm({ ...form, description: e.target.value })}
                />
              </div>
            </div>
          </div>

          {form.rule_type === 'Tiered Monthly' && (
            <div className="space-y-3">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="text-sm font-semibold text-gray-900">{t('commission.form.monthlyTiers')}</h3>
                  <p className="text-xs text-gray-500">{t('commission.form.tiersHint')}</p>
                </div>
                <button type="button" className="btn-secondary text-sm inline-flex items-center gap-1" onClick={addTier}>
                  <Plus className="w-4 h-4" />
                  {t('commission.form.addTier')}
                </button>
              </div>

              <div className="overflow-x-auto border border-gray-200 rounded-lg">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 text-xs uppercase text-gray-500">
                    <tr>
                      <th className="px-3 py-2 text-left font-medium">{t('commission.form.fromAmount')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('commission.form.toAmount')}</th>
                      <th className="px-3 py-2 text-left font-medium">{t('commission.form.rate')}</th>
                      <th className="px-3 py-2 w-10" />
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {tiers.map((tier, index) => (
                      <tr key={index}>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            className="input-field"
                            value={tier.from_amount}
                            onChange={(e) => updateTier(index, { from_amount: parseFloat(e.target.value) || 0 })}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            min="0"
                            className="input-field"
                            placeholder={t('commission.form.noLimit')}
                            value={tier.to_amount ?? ''}
                            onChange={(e) =>
                              updateTier(index, {
                                to_amount: e.target.value === '' ? undefined : parseFloat(e.target.value) || 0,
                              })
                            }
                          />
                        </td>
                        <td className="px-3 py-2">
                          <input
                            type="number"
                            step="0.01"
                            min="0"
                            className="input-field"
                            value={tier.rate}
                            onChange={(e) => updateTier(index, { rate: parseFloat(e.target.value) || 0 })}
                          />
                        </td>
                        <td className="px-3 py-2">
                          <button
                            type="button"
                            className="p-1.5 text-red-500 hover:bg-red-50 rounded-lg"
                            onClick={() => removeTier(index)}
                            disabled={tiers.length <= 1}
                          >
                            <Trash2 className="w-4 h-4" />
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <label className="inline-flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setForm({ ...form, is_active: e.target.checked })}
            />
            {t('commission.form.active')}
          </label>

          {error && (
            <div className="flex items-start gap-2 text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg p-3">
              <AlertCircle className="w-4 h-4 mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          )}
        </form>

        <div className="px-5 py-4 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" className="btn-secondary" onClick={onClose}>
            {t('commission.form.cancel')}
          </button>
          <button
            type="button"
            className="btn-primary inline-flex items-center gap-2"
            disabled={saveMutation.isPending}
            onClick={handleSubmit}
          >
            {saveMutation.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
            {editing ? t('commission.form.save') : t('commission.form.create')}
          </button>
        </div>
      </div>
    </div>
  );
}
